import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';

/** Tabs of the template modal's docked sidebar, addressable from a card. */
export type DocumentFieldsTab = 'fields' | 'conditionalTexts';

const documentFieldsTabs = ['fields', 'conditionalTexts'] as const;

/**
 * What the mapping panel is showing for the placeholder it opened on: a field
 * to pick, a signer to pick, or the form for a field that doesn't exist yet.
 */
export type MappingPanelKind = 'field' | 'signer' | 'createField';

const mappingPanelKinds = ['field', 'signer', 'createField'] as const;

/** Search param holding the template the modal is open on. */
export const TEMPLATE_SEARCH_PARAM = 'template';
/** Search param holding which of the modal's sidebar tabs is showing. */
const TEMPLATE_TAB_SEARCH_PARAM = 'templateTab';
/** Search param holding which picker the mapping panel is showing. */
const MAPPING_PANEL_SEARCH_PARAM = 'mapping';
/** Search param holding the placeholder the mapping panel is mapping. */
const MAPPING_PLACEHOLDER_SEARCH_PARAM = 'mappingPlaceholder';

// Opening and closing the modal are navigations the user can undo, so they
// push; switching tabs inside it adjusts the modal in place and replaces.
const parseAsTemplateId = parseAsString.withOptions({ history: 'push' });
const parseAsTemplateTab = parseAsStringLiteral(documentFieldsTabs);
const parseAsMappingPanel = parseAsStringLiteral(mappingPanelKinds);

/**
 * The template a builder is editing lives in the URL rather than in component
 * state: the modal opens over whichever builder section is showing, so a
 * reload, a deep link, or browser Back all have to land back on the same
 * template and tab.
 */
export const useDocumentTemplateModal = () => {
  const [templateId, setTemplateId] = useQueryState(
    TEMPLATE_SEARCH_PARAM,
    parseAsTemplateId,
  );
  const [templateTab, setTemplateTab] = useQueryState(
    TEMPLATE_TAB_SEARCH_PARAM,
    parseAsTemplateTab,
  );
  const [mappingPanel, setMappingPanel] = useQueryState(
    MAPPING_PANEL_SEARCH_PARAM,
    parseAsMappingPanel,
  );
  const [mappingPlaceholder, setMappingPlaceholder] = useQueryState(
    MAPPING_PLACEHOLDER_SEARCH_PARAM,
    parseAsString,
  );

  const openTemplate = useCallback(
    (documentId: string, tab?: DocumentFieldsTab) => {
      setTemplateId(documentId);
      setTemplateTab(tab ?? null);
    },
    [setTemplateId, setTemplateTab],
  );

  const closeMappingPanel = useCallback(() => {
    setMappingPanel(null);
    setMappingPlaceholder(null);
  }, [setMappingPanel, setMappingPlaceholder]);

  // The panel is a step within the open template, not a place of its own: it
  // replaces so Back leaves the template rather than closing the panel first,
  // but it stays in the URL so a reload lands the author back in it.
  const openMappingPanel = useCallback(
    (kind: MappingPanelKind, placeholder: string) => {
      setMappingPanel(kind, { history: 'replace', shallow: true });
      setMappingPlaceholder(placeholder, {
        history: 'replace',
        shallow: true,
      });
    },
    [setMappingPanel, setMappingPlaceholder],
  );

  const closeTemplate = useCallback(() => {
    setTemplateId(null);
    setTemplateTab(null);
    setMappingPanel(null);
    setMappingPlaceholder(null);
  }, [setMappingPanel, setMappingPlaceholder, setTemplateId, setTemplateTab]);

  const selectTab = useCallback(
    (tab: DocumentFieldsTab) =>
      setTemplateTab(tab, { history: 'replace', shallow: true }),
    [setTemplateTab],
  );

  return useMemo(
    () => ({
      templateId,
      templateTab,
      // A panel with no placeholder to map has nothing to show, so the pair is
      // only honoured together (e.g. a hand-edited URL carrying just one).
      mappingPanel: mappingPlaceholder ? mappingPanel : null,
      mappingPlaceholder: mappingPanel ? mappingPlaceholder : null,
      openTemplate,
      closeTemplate,
      openMappingPanel,
      closeMappingPanel,
      selectTab,
    }),
    [
      closeMappingPanel,
      closeTemplate,
      mappingPanel,
      mappingPlaceholder,
      openMappingPanel,
      openTemplate,
      selectTab,
      templateId,
      templateTab,
    ],
  );
};
